import * as React from 'react';
import { Ellipsis } from '@common/components/Ellipsis';
import { Flex } from '@common/components/Flex';
import { Button } from '@common/components/Button';
import { Switch } from '@common/components/switch';
import { MessageConfirmParams } from '@common/hooks/message';
import { I18n } from 'react-redux-i18n';
import { InfoMessage } from '@common/components/info-message';

import './page.styles.scss';

export type PageItem = {
  id: number | string;
  title: string;
  enabled: boolean;
};

export type PageProps<Item extends PageItem> = {
  data: Item;
  features: {
    delete: boolean;
    edit: boolean;
    changeShowStatus: boolean;
    copy: boolean;
  };
  showDeleteConfirmMessage: (props: MessageConfirmParams) => void;
  onEdit: (item: Item) => void;
  onDelete: (item: Item) => void;
  onCopy: (item: Item['id']) => void | Promise<Item>;
  onShowStatusChange: (item: Item, status: boolean) => void;
  showErrorMessage: (msg: string) => void;
};

export function Page<T extends PageItem>(props: PageProps<T>) {
  const { data, features, onEdit, onShowStatusChange, showDeleteConfirmMessage, onDelete, onCopy, showErrorMessage } = props;
  const hasAnyFeature = Object.values(features).some((x) => x);
  const [isCopyLoading, setIsCopyLoading] = React.useState(false);

  const handleShowStatusChange = React.useCallback((isVisible: boolean) => {
    onShowStatusChange(data, isVisible);
  }, [onShowStatusChange, data]);

  const handleEditClick = React.useCallback(() => {
    onEdit(data);
  }, [onEdit, data]);

  const handleDeleteClick = React.useCallback(() => {
    showDeleteConfirmMessage({
      header: I18n.t('Confirmation Message'),
      content: <InfoMessage>{I18n.t('Are you sure you want to delete this page?')}</InfoMessage>,
      confirmText: I18n.t('Yes'),
      cancelText: I18n.t('No'),
      onCustomOk: () => {
        onDelete(data);
      }
    });
  }, [onDelete, showDeleteConfirmMessage, data]);

  const handlePageCopy = React.useCallback(async () => {
    try {
      setIsCopyLoading(true);
      await onCopy(data.id);
    } finally {
      setIsCopyLoading(false)
    }
  }, [onCopy, data, showErrorMessage]);

  return (
    <>
      <Ellipsis text={data.title} width="calc(100% - 60px)" />
      {hasAnyFeature && (
        <Flex alignItems="center">
          {features.edit && (
            <Button 
              className="FhqDesignToolQuestionnaireDesignerPage__action"  
              variant="text" 
              onClick={handleEditClick}
            >
              <i className="fa fa-pencil" />
            </Button>
          )}
          {features.copy && (
            <Button
              className="FhqDesignToolQuestionnaireDesignerPage__action"
              variant="text"
              onClick={handlePageCopy}
              disabled={isCopyLoading}
            >
              {isCopyLoading ? (
                <i className="fa fa-spinner" />
              ) : (
                <i className="fa fa-copy" />
              )}
            </Button>
          )}
          {features.delete && (
            <Button 
              className="FhqDesignToolQuestionnaireDesignerPage__action"
              variant="text"
              onClick={handleDeleteClick}
            >
              <i className="fa fa-trash" />
            </Button>
          )}
          {features.changeShowStatus && (
            <Switch
              className="FhqDesignToolQuestionnaireDesignerPage__action"
              checked={data.enabled}
              onChange={handleShowStatusChange}
            />
          )}
        </Flex>
      )}
    </>
  );
};